<script lang="ts" setup>
import type { DrawerTextLink, DrawerTile } from '#core/utils/nav-drawers'
import { socialLinks } from '#core/utils/external-links'
import { Badge } from '@forgd/ui-kit/badge'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { Button } from '@forgd/ui-kit/button'
import { useBodyScrollLock } from '@forgd/ui-kit/sheet'
import { Bars3Icon, ChevronDownIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
// resolves only in apps/ui; typed elsewhere by the fallback contract in #core/types/advisor-portal
import { useAdvisor } from '#advisor-portal/state/useAdvisor'

const auth = useAuth()
const advisor = useAdvisor()
const route = useRoute()
const NuxtLink = resolveComponent('NuxtLink')
const { open: openLoginModal } = useLoginModal()

const accountLabel = computed(() => auth.project?.ticker || auth.project?.name || 'Account')

const homeLink = useHomeNavLink()

const drawerSectionIds = ['home-research', 'home-about'] as const

// Two mutually-exclusive mobile overlays: the hamburger menu and the account /
// project panel. Opening one closes the other so only a single overlay (and a
// single scroll lock) is ever active.
const open = ref(false)
const accountOpen = ref(false)
const drawers = useHomeNavDrawers()
const drawerSections = computed(() => drawerSectionIds.map(id => ({ id, drawer: drawers.value[id] })))

function toggleMenu() {
  accountOpen.value = false
  open.value = !open.value
}
function toggleAccount() {
  open.value = false
  accountOpen.value = !accountOpen.value
}

const menuButton = ref<HTMLButtonElement>()
const accountButton = ref<HTMLButtonElement>()
const drawerEl = ref<HTMLElement>()
const panelEl = ref<HTMLElement>()

const { activate: activateMenu, deactivate: deactivateMenu } = useFocusTrap(drawerEl, {
  allowOutsideClick: true,
  setReturnFocus: () => menuButton.value ?? false,
  onDeactivate: () => {
    open.value = false
  },
})
const { activate: activateAccount, deactivate: deactivateAccount } = useFocusTrap(panelEl, {
  allowOutsideClick: true,
  setReturnFocus: () => accountButton.value ?? false,
  onDeactivate: () => {
    accountOpen.value = false
  },
})

watch(() => route.fullPath, () => {
  open.value = false
  accountOpen.value = false
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greaterOrEqual('md')
watch(isDesktop, (value) => {
  if (value) {
    open.value = false
    accountOpen.value = false
  }
})

const isScrollLocked = useBodyScrollLock()
watchEffect(() => {
  isScrollLocked.value = open.value || accountOpen.value
})
watch(open, async (value) => {
  if (value) {
    await nextTick()
    activateMenu()
  }
  else {
    deactivateMenu()
  }
})
watch(accountOpen, async (value) => {
  if (value) {
    await nextTick()
    activateAccount()
  }
  else {
    deactivateAccount()
  }
})

function tag(item: { to?: string, external?: boolean }) {
  if (!item.to) {
    return 'div'
  }
  return item.external ? 'a' : NuxtLink
}
function linkProps(item: { to?: string, external?: boolean }) {
  if (!item.to) {
    return {}
  }
  return item.external ? { href: item.to, target: '_blank', rel: 'noopener noreferrer' } : { to: item.to }
}
</script>

<template>
  <div
    data-el="CorePageHeaderMobile"
    class="md:hidden sticky inset-x-0 top-0 z-40 flex h-14 items-center border-b border-default bg-neutral-200"
  >
    <NuxtLink :to="homeLink.to" aria-label="Forgd home" class="flex min-w-0 flex-1 items-center px-4 text-primal">
      <img src="/app/logo-dark.svg" alt="Forgd" class="w-[100px] shrink-0">
    </NuxtLink>
    <ClientOnly>
      <div class="flex items-center gap-2 pr-3">
        <template v-if="auth.isLoggedIn">
          <button
            ref="accountButton"
            type="button"
            class="flex h-8 items-center gap-1.5 rounded-lg border border-neutral-600 px-2 text-xs font-semibold text-primal"
            :aria-label="`${accountLabel} account menu`"
            :aria-expanded="accountOpen"
            @click="toggleAccount"
          >
            <ProjectAvatar v-if="auth.project?.image" :src="auth.project.image" size="2xs" bordered />
            <span class="max-w-[90px] truncate uppercase">{{ accountLabel }}</span>
            <component :is="accountOpen ? XMarkIcon : ChevronDownIcon" class="size-4 shrink-0" />
          </button>
          <CoreAppNotifications />
        </template>
        <Button v-else size="sm" @click="openLoginModal()">
          Login/Register
        </Button>
      </div>
    </ClientOnly>
    <button
      ref="menuButton"
      type="button"
      class="flex h-full w-11 shrink-0 items-center justify-center border-l border-default text-primal"
      :aria-label="open ? 'Close menu' : 'Open menu'"
      :aria-expanded="open"
      @click="toggleMenu"
    >
      <component :is="open ? XMarkIcon : Bars3Icon" class="size-5" />
    </button>
  </div>

  <ClientOnly>
    <Teleport to="body">
      <Transition
        enter-active-class="transform transition ease-in-out duration-300"
        leave-active-class="transform transition ease-in-out duration-200"
        enter-from-class="translate-x-full"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="accountOpen"
          ref="panelEl"
          class="fixed inset-x-0 top-14 bottom-0 z-[100] md:hidden overflow-y-auto bg-neutral-200 font-display text-primal"
          role="dialog"
          aria-modal="true"
          aria-label="Account menu"
        >
          <CoreHeaderProfileContent @close="accountOpen = false" />
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition
        enter-active-class="transform transition ease-in-out duration-300"
        leave-active-class="transform transition ease-in-out duration-200"
        enter-from-class="translate-x-full"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="open"
          ref="drawerEl"
          class="fixed inset-x-0 top-14 bottom-0 z-[100] md:hidden overflow-y-auto bg-neutral-200 font-display text-primal"
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
        >
          <NuxtLink
            :to="homeLink.to"
            class="flex items-center border-b border-default px-4 py-4 text-sm font-semibold leading-none"
            @click="open = false"
          >
            {{ homeLink.label }}
          </NuxtLink>

          <NuxtLink
            v-if="advisor.isAdvisorDefault"
            to="/advisor-portal/teammates"
            class="flex items-center border-b border-default px-4 py-4 text-sm font-semibold leading-none"
            @click="open = false"
          >
            Manage Teammates
          </NuxtLink>
          <section v-else class="flex flex-col gap-4 border-b border-default p-4">
            <div class="flex items-center gap-2">
              <span class="flex-1 text-sm font-semibold leading-none">Free Tools</span>
              <span class="inline-flex h-5 shrink-0 items-center rounded-full border border-secondary/30 bg-secondary/10 px-2 font-mono text-xs font-medium tracking-[-0.2px] text-secondary">
                Available on Desktop Only
              </span>
            </div>
            <CoreConsultationCard v-if="!auth.isAdvisor" @click="open = false" />
          </section>

          <section
            v-for="{ id, drawer } in drawerSections"
            :key="id"
            class="flex flex-col gap-4 border-b border-default p-4"
          >
            <span class="text-sm font-semibold leading-none">{{ drawer.title }}</span>

            <div v-if="drawer.tilesHeading" class="text-xs text-placeholder">
              {{ drawer.tilesHeading }}
            </div>
            <component
              :is="tag(tile)"
              v-for="tile in drawer.tiles"
              :key="tile.label"
              v-bind="linkProps(tile as DrawerTile)"
              class="flex items-start gap-4 rounded-lg px-2 py-1 transition-colors"
              :class="tile.to ? 'hover:bg-neutral-600/30' : 'cursor-default'"
              @click="tile.to && (open = false)"
            >
              <img :src="tile.icon" alt="" class="size-10 shrink-0">
              <div class="flex flex-col gap-1">
                <span class="text-xs font-semibold leading-none text-primal">{{ tile.label }}</span>
                <p class="text-2xs text-secondary">
                  {{ tile.description }}
                </p>
                <Badge v-if="tile.badge" variant="gray" class="w-fit" size="sm">
                  {{ tile.badge }}
                </Badge>
              </div>
            </component>

            <div v-if="drawer.links" class="-mx-4 border-t border-default px-4 pt-2">
              <component
                :is="tag(link)"
                v-for="link in (drawer.links as DrawerTextLink[])"
                :key="link.label"
                v-bind="linkProps(link)"
                class="block rounded-lg py-4 text-xs font-semibold leading-none text-primal transition-colors hover:bg-neutral-600/30"
                @click="open = false"
              >
                {{ link.label }}
              </component>
            </div>
          </section>

          <div class="flex items-center gap-9 px-4 py-5">
            <a
              v-for="social in socialLinks"
              :key="social.label"
              :href="social.href"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="social.label"
              class="flex size-4 items-center justify-center transition-opacity hover:opacity-70"
            >
              <img :src="social.icon" :alt="social.label" class="size-4">
            </a>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
